var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment } from 'react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Button, Colors, Heading, HeadingLevel, Icon, Link, Text, } from '@zapier/design-system';
import UniversalNavLink from '../UniversalNavLink';
import { usageLink } from '../../constants';
import { useQueryAccount, useQueryAccountUsage, useUniversalNavContext, } from '../../hooks';
import { useQueryAccountPlan } from '../../../universal-layout/hooks';
import UniversalSidebarError from './UniversalSidebarError';
import UniversalSidebarSubheaderButton from './UniversalSidebarSubheaderButton';
import UniversalSidebarUsage from './UniversalSidebarUsage/UniversalSidebarUsage';
import UniversalSidebar, { isLinkSelected } from './UniversalSidebar';
const infinitize = (number) => {
    return number === -1 ? Infinity : number;
};
const MaybeCta = (props) => {
    const isFree = props.planAmount === 0;
    const isNearTaskLimit = props.numTasks / props.numTasksMax >= 0.8;
    const isNearZapLimit = props.numZaps / props.numZapsMax >= 0.8;
    return isFree || isNearTaskLimit || isNearZapLimit ? (isolateComponent(Button, { color: "secondary", href: props.upgradeLink, isFullWidth: true, size: "small" }, "Upgrade plan")) : null;
};
const Styles = labelStyles('UniversalSidebarContainer', {
    footerWrapper: {
        overflow: 'hidden',
        padding: 10,
    },
    tOSWrapper: {
        whiteSpace: 'initial',
        background: Colors.neutral200,
        border: `1px solid ${Colors.neutral300}`,
        borderRadius: 10,
        padding: 15,
    },
    tOSHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 20,
    },
    tOSHeading: {
        display: 'flex',
        alignItems: 'center',
    },
    tOSDismissalIconButton: {
        display: 'flex',
        alignItems: 'center',
        '&:focus': {
            outline: `1px solid ${Colors.blue}`,
        },
    },
    tOSDismissalButton: {
        color: `${Colors.blue}`,
        textDecoration: 'underline',
        '&:focus': {
            outline: `1px solid ${Colors.blue}`,
        },
    },
    divider: {
        border: `1px solid ${Colors.neutral300}`,
        marginTop: 10,
        marginBottom: 10,
    },
});
const MaybeTOS = () => {
    const LATEST_VERSION_OF_TERMS = 'design-system-tos-notification-2022-11-15';
    const hasUserDismissedLatestToS = localStorage.getItem('versionOfTermsDismissed') ===
        LATEST_VERSION_OF_TERMS || false;
    const handleDismiss = () => {
        localStorage.setItem('versionOfTermsDismissed', LATEST_VERSION_OF_TERMS);
    };
    const { getFullyQualifiedURL, shouldRenderTOS } = useUniversalNavContext();
    return shouldRenderTOS && hasUserDismissedLatestToS === false ? (isolateComponent(Fragment, null,
        isolateComponent("hr", { css: Styles.divider }),
        isolateComponent("div", { css: Styles.tOSWrapper },
            isolateComponent("div", { css: Styles.tOSHeader },
                isolateComponent("div", { css: Styles.tOSHeading },
                    isolateComponent(Icon, { ariaHidden: true, color: "blueJeans", name: "alertInfoFill", size: 18 }),
                    isolateComponent(HeadingLevel, null,
                        isolateComponent(Text, { margin: "0 0 0 5px", tag: Heading, type: "smallPrint1Semibold" }, "Updated Terms"))),
                isolateComponent("button", { "aria-label": "Dismiss notification", css: Styles.tOSDismissalIconButton, onClick: handleDismiss, type: "button" },
                    isolateComponent(Icon, { ariaHidden: true, color: "neutral800", name: "formX", size: 24 }))),
            isolateComponent(Text, { tag: "p", type: "smallPrint1" },
                "We updated the",
                ' ',
                isolateComponent(Link, { href: getFullyQualifiedURL('/legal/terms-of-service') }, "Zapier Terms of Service"),
                ' ',
                "on November 15, 2022. For existing users, these updated terms will be effective beginning on December 15, 2022. By continuing to use Zapier, you agree to these updated terms.",
                ' ',
                isolateComponent("button", { css: Styles.tOSDismissalButton, type: "button", onClick: handleDismiss }, "Dismiss"))))) : null;
};
const FooterWrapper = ({ children }) => (isolateComponent("div", { css: Styles.footerWrapper }, children));
const UniversalSidebarContainer = (_a) => {
    var _b;
    var { currentAccountId } = _a, props = __rest(_a, ["currentAccountId"]);
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const { data: accountData, isError: isAccountError } = useQueryAccount(currentAccountId);
    const { data: accountUsageData, isError: isAccountUsageError, } = useQueryAccountUsage(currentAccountId);
    const { data: accountPlanData, isError: isAccountPlanError, } = useQueryAccountPlan(currentAccountId, (_b = accountData === null || accountData === void 0 ? void 0 : accountData.plan) === null || _b === void 0 ? void 0 : _b.id);
    const renderedUsageLink = (isolateComponent(UniversalNavLink, { href: usageLink.href, icon: typeof usageLink.icon === 'object'
            ? usageLink.icon.selected
            : usageLink.icon, isCollapsed: props.isCollapsed, isSelected: isLinkSelected(usageLink.href || '', props.selectedHref), isSidebar: true, text: usageLink.text }));
    const renderFooter = () => {
        if (!accountData)
            return null;
        if (props.isCollapsed) {
            return renderedUsageLink;
        }
        if (isAccountError || isAccountPlanError || isAccountUsageError) {
            return (isolateComponent(React.Fragment, null,
                renderedUsageLink,
                isolateComponent(FooterWrapper, null,
                    isolateComponent(UniversalSidebarError, null, "An error has occurred"),
                    isolateComponent(MaybeTOS, null))));
        }
        if (!accountUsageData || !accountPlanData) {
            return null;
        }
        const { plan, isTrialing, trialEndDate, isTrialingTeams, teamTrialEndDate, isTrialingCompanies, companiesTrialEndDate, } = accountData;
        const { meteredPricingTiers: tiers } = accountPlanData;
        const freeTier = tiers && tiers.find((tier) => tier.pricePerTask === 0);
        const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
        const normalizedUsage = {
            isPlanMetered: plan.hasMeteredBilling,
            numTasks: accountUsageData.tasks.total,
            numFreeTasksMax,
            numTasksMax: infinitize(accountUsageData.tasks.limit),
            numZaps: accountUsageData.zaps.on,
            numZapsMax: infinitize(accountUsageData.zaps.limit),
        };
        const trialPlanName = isTrialingTeams
            ? 'Team'
            : isTrialingCompanies
                ? 'Company'
                : isTrialing
                    ? 'Professional'
                    : undefined;
        const trialPlanEndDate = isTrialingTeams
            ? teamTrialEndDate
            : isTrialingCompanies
                ? companiesTrialEndDate
                : trialEndDate;
        return (isolateComponent(FooterWrapper, null,
            isolateComponent(UniversalSidebarUsage, Object.assign({}, normalizedUsage, { resetDate: accountUsageData.periodEnd, title: `${plan.name} Plan`, trialPlanName: trialPlanName, trialPlanEndDate: trialPlanEndDate !== null && trialPlanEndDate !== void 0 ? trialPlanEndDate : undefined }),
                isolateComponent(MaybeCta, Object.assign({}, normalizedUsage, { planAmount: plan.amount, upgradeLink: getFullyQualifiedURL('/app/pricing') })),
                isolateComponent(MaybeTOS, null))));
    };
    return (isolateComponent(UniversalSidebar, Object.assign({}, props, { logoAriaLabel: "Go to dashboard", logoHref: getFullyQualifiedURL('/app/dashboard'), renderFooter: renderFooter, renderSubheader: () => (isolateComponent(UniversalSidebarSubheaderButton, { href: getFullyQualifiedURL('/webintent/create-zap?entry-point-method=make_a_zap_call_to_action'), iconName: "formAdd", isCollapsed: props.isCollapsed, text: "Create Zap" })) })));
};
export default UniversalSidebarContainer;
//# sourceMappingURL=UniversalSidebarContainer.js.map
// @zapier/universal-layout@8.3.1 (41acce620)
